// Nueva reserva en página completa (no modal): reemplaza el listado de
// Reservas mientras se captura y vuelve a él al cancelar o reservar.
// Izquierda: estadía → huésped → asignación/facturación. Derecha (fija): la
// cotización se recalcula sola al cambiar tipo, plan, fechas u ocupantes.
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ArrowLeft, BedDouble, Building2, CalendarDays, UserRound } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { usePermissions } from '@/lib/hooks/usePermissions';
import {
  Campo, fieldCls, money, hotelRpc, hotelTabla, QK, hoyISO, sumarDias, fmtFecha, CANALES,
  type TipoHabitacion, type Plan, type Habitacion, type Cotizacion, type Rack,
} from './shared';
import { BuscadorCliente, TIPOS_IDENT, useClientes, type ClienteOpt } from './ClienteHuesped';
import { AvisosPerfil, type PerfilHuesped } from './Huesped360';

function Seccion({ n, titulo, icon, children }: { n: number; titulo: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-xs text-primary-foreground">{n}</span>
          {icon} {titulo}
        </CardTitle>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

// `inicial` prellena la estadía (p. ej. desde una celda libre del rack).
export interface NuevaReservaInicial { tipoId?: string; habitacionId?: string; llegada?: string; salida?: string }

export function NuevaReserva({ onClose, onCreated, inicial, volverLabel = 'Reservas' }: {
  onClose: () => void; onCreated: (id: string) => void; inicial?: NuevaReservaInicial; volverLabel?: string;
}) {
  const qc = useQueryClient();
  const { isAdmin } = usePermissions();

  const { data: tipos = [] } = useQuery<TipoHabitacion[]>({ queryKey: [QK, 'tipos'], queryFn: () => hotelTabla<TipoHabitacion>('tipos_habitacion', 'es_activo=eq.true&order=orden') });
  const { data: planes = [] } = useQuery<Plan[]>({ queryKey: [QK, 'planes'], queryFn: () => hotelTabla<Plan>('planes', 'es_activo=eq.true&order=orden') });
  const { data: habitaciones = [] } = useQuery<Habitacion[]>({ queryKey: [QK, 'habitaciones'], queryFn: () => hotelTabla<Habitacion>('habitaciones', 'es_activo=eq.true&order=orden') });
  const clientes = useClientes();
  const [clienteSel, setClienteSel] = useState<ClienteOpt | null>(null);
  const { data: fichaSel } = useQuery<{ perfil: Partial<PerfilHuesped>; resumen: { estadias: number } }>({
    queryKey: [QK, 'ficha', clienteSel?.id], queryFn: () => hotelRpc('huesped_ficha', { _partner: clienteSel?.id }), enabled: !!clienteSel,
  });

  const [tipoId, setTipoId] = useState(inicial?.tipoId ?? '');
  const [planId, setPlanId] = useState('');
  const [llegada, setLlegada] = useState(inicial?.llegada ?? hoyISO());
  const [salida, setSalida] = useState(inicial?.salida ?? sumarDias(inicial?.llegada ?? hoyISO(), 1));
  const [adultos, setAdultos] = useState(1);
  const [ninos, setNinos] = useState(0);
  const [forzar, setForzar] = useState(false);
  const [habitacionId, setHabitacionId] = useState(inicial?.habitacionId ?? '');
  const [empresaId, setEmpresaId] = useState('');
  const [canal, setCanal] = useState('recepcion');
  const [notas, setNotas] = useState('');
  const [horaLlegada, setHoraLlegada] = useState('');
  const [garantizada, setGarantizada] = useState(false);
  const [huesped, setHuesped] = useState({ nombre: '', identificacion: '', tipo_identificacion: 'CEDULA', email: '', telefono: '', nacionalidad: '' });

  const fechasValidas = !!llegada && !!salida && salida > llegada;
  const tipoSel = tipos.find((t) => t.id === tipoId);

  // Cotización automática: cada cambio de parámetros es otra consulta
  const { data: cot = null, isFetching: cotizando, error: errCot } = useQuery<Cotizacion>({
    queryKey: [QK, 'cotizar', tipoId, planId, llegada, salida, adultos, ninos],
    queryFn: () => hotelRpc<Cotizacion>('cotizar', { _tipo: tipoId, _plan: planId || null, _llegada: llegada, _salida: salida, _adultos: adultos, _ninos: ninos }),
    enabled: !!tipoId && fechasValidas,
    retry: false,
  });
  // Al cambiar la estadía se pierde el «forzar» y la habitación elegida (no al
  // montar: la habitación puede venir prellenada desde el rack).
  const estadiaPrev = useRef(`${tipoId}|${llegada}|${salida}`);
  useEffect(() => {
    const k = `${tipoId}|${llegada}|${salida}`;
    if (k === estadiaPrev.current) return;
    estadiaPrev.current = k;
    setForzar(false); setHabitacionId('');
  }, [tipoId, llegada, salida]);

  const { data: rack } = useQuery<Rack | null>({
    queryKey: [QK, 'rack-nueva', llegada, salida],
    queryFn: () => hotelRpc<Rack>('rack', { _desde: llegada, _hasta: salida }),
    enabled: !!tipoId && fechasValidas,
  });
  const habitacionesLibres = useMemo(() => {
    if (!rack || !tipoId) return [];
    const ocupadas = new Set<string>();
    rack.reservas.forEach((x) => {
      if (x.estado === 'cancelada' || x.estado === 'no_show' || !x.habitacion_id) return;
      if (x.llegada < salida && llegada < x.salida) ocupadas.add(x.habitacion_id);
    });
    rack.bloqueos.forEach((b) => {
      if (!(b.desde < salida && llegada < b.hasta)) return;
      if (b.habitacion_id) ocupadas.add(b.habitacion_id);
      else if (!b.tipo_id || b.tipo_id === tipoId) habitaciones.filter((h) => h.tipo_id === tipoId).forEach((h) => ocupadas.add(h.id));
    });
    return habitaciones.filter((h) => h.tipo_id === tipoId && !ocupadas.has(h.id));
  }, [rack, tipoId, habitaciones, llegada, salida]);

  // Identificación digitada que ya es de un cliente: ofrecer usarlo
  const coincide = useMemo(() => {
    const id = huesped.identificacion.trim().toUpperCase();
    return !clienteSel && id.length >= 5 ? clientes.find((c) => (c.identificacion ?? '').toUpperCase() === id) ?? null : null;
  }, [huesped.identificacion, clientes, clienteSel]);
  const elegirCliente = (c: ClienteOpt | null) => {
    setClienteSel(c);
    if (c) {
      const id = c.identificacion ?? '';
      setHuesped((h) => ({ ...h, nombre: c.razon_social, identificacion: id,
        tipo_identificacion: /^\d{13}$/.test(id) ? 'RUC' : /^\d{10}$/.test(id) ? 'CEDULA' : 'PASAPORTE',
        email: h.email || c.email || '', telefono: h.telefono || c.celular || '' }));
    } else setHuesped((h) => ({ ...h, nombre: '', identificacion: '' }));
  };

  const faltan: string[] = [];
  if (!tipoId) faltan.push('el tipo de habitación');
  if (!fechasValidas) faltan.push('fechas válidas');
  if (!huesped.nombre.trim()) faltan.push('el nombre del huésped');
  const puedeReservar = !!cot && faltan.length === 0 && !cotizando && (cot.errores.length === 0 || (forzar && isAdmin()));

  const crear = useMutation({
    mutationFn: () => hotelRpc<{ id: string; codigo: string; total: number }>('reserva_crear', {
      _d: {
        tipo_id: tipoId, plan_id: planId || null, llegada, salida, adultos, ninos,
        habitacion_id: habitacionId || null,
        notas: notas.trim() || null,
        hora_llegada: horaLlegada || null, llegada_garantizada: garantizada,
        canal,
        facturar_partner_id: empresaId || null,
        forzar: cot && cot.errores.length > 0 ? forzar : undefined,
        huesped: {
          partner_id: clienteSel?.id ?? null,
          nombre: huesped.nombre.trim(),
          identificacion: huesped.identificacion.trim() || null,
          tipo_identificacion: huesped.tipo_identificacion,
          email: huesped.email.trim() || null,
          telefono: huesped.telefono.trim() || null,
          nacionalidad: huesped.nacionalidad.trim() || null,
        },
      },
    }),
    onSuccess: (r) => {
      toast.success(`Reserva ${r.codigo} creada · ${money(r.total)}`);
      void qc.invalidateQueries({ queryKey: [QK] });
      onCreated(r.id);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const noches = fechasValidas ? Math.round((new Date(`${salida}T12:00:00`).getTime() - new Date(`${llegada}T12:00:00`).getTime()) / 86400000) : 0;

  return (
    <div className="space-y-4 text-sm">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={onClose}><ArrowLeft className="mr-1 h-4 w-4" /> {volverLabel}</Button>
          <h1 className="text-xl font-semibold">Nueva reserva</h1>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <Seccion n={1} titulo="Estadía" icon={<CalendarDays className="h-4 w-4" />}>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
              <Campo label="Llegada"><input type="date" className={fieldCls} value={llegada}
                onChange={(e) => { const v = e.target.value; setLlegada(v); if (v && salida <= v) setSalida(sumarDias(v, 1)); }} /></Campo>
              <Campo label="Salida"><input type="date" className={fieldCls} min={sumarDias(llegada, 1)} value={salida} onChange={(e) => setSalida(e.target.value)} /></Campo>
              <div className="flex items-end pb-2 text-muted-foreground">{noches > 0 ? `${noches} noche${noches === 1 ? '' : 's'}` : 'La salida debe ser posterior a la llegada'}</div>
              <Campo label="Adultos"><input type="number" min={1} className={fieldCls} value={adultos} onChange={(e) => setAdultos(Math.max(1, Number(e.target.value) || 1))} /></Campo>
              <Campo label="Niños"><input type="number" min={0} className={fieldCls} value={ninos} onChange={(e) => setNinos(Math.max(0, Number(e.target.value) || 0))} /></Campo>
              <Campo label="Hora estimada de llegada"><input type="time" className={fieldCls} value={horaLlegada} onChange={(e) => setHoraLlegada(e.target.value)} /></Campo>
              <label className="col-span-2 flex items-center gap-2 text-xs md:col-span-3" title="El cierre nocturno no la marca como no-show el día de llegada; si llega después del cierre, en el check-in se carga la noche guardada">
                <input type="checkbox" checked={garantizada} onChange={(e) => setGarantizada(e.target.checked)} /> Llegada tardía garantizada (p. ej. llega en la última lancha o vuelo)
              </label>
            </div>
            <div className="mt-4 space-y-1">
              <span className="text-xs font-medium text-muted-foreground">Tipo de habitación *</span>
              <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
                {tipos.map((t) => {
                  const cabe = adultos <= t.capacidad_adultos && ninos <= t.capacidad_ninos + (t.capacidad_adultos - adultos);
                  return (
                    <button key={t.id} type="button" onClick={() => setTipoId(t.id)}
                      className={`rounded-md border p-3 text-left transition ${tipoId === t.id ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'hover:bg-muted/50'} ${cabe ? '' : 'opacity-60'}`}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-medium">{t.nombre}</span>
                        <span className="font-mono text-xs">{money(t.tarifa_base)}</span>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {t.capacidad_adultos} adultos{t.capacidad_ninos ? ` + ${t.capacidad_ninos} niños` : ''}{t.camas ? ` · ${t.camas}` : ''}
                      </div>
                      {!cabe && <div className="text-xs text-amber-600">Supera la capacidad</div>}
                    </button>
                  );
                })}
                {tipos.length === 0 && <p className="text-muted-foreground">No hay tipos de habitación activos (Hotel → Catálogo).</p>}
              </div>
            </div>
            <div className="mt-4 space-y-1">
              <span className="text-xs font-medium text-muted-foreground">Plan / tarifa</span>
              <div className="flex flex-wrap gap-2">
                <button type="button" onClick={() => setPlanId('')}
                  className={`rounded-full border px-3 py-1 text-xs ${planId === '' ? 'border-primary bg-primary text-primary-foreground' : 'hover:bg-muted'}`}>Tarifa estándar</button>
                {planes.map((p) => (
                  <button key={p.id} type="button" onClick={() => setPlanId(p.id)}
                    className={`rounded-full border px-3 py-1 text-xs ${planId === p.id ? 'border-primary bg-primary text-primary-foreground' : 'hover:bg-muted'}`}>
                    {p.nombre}
                  </button>
                ))}
              </div>
            </div>
          </Seccion>

          <Seccion n={2} titulo="Huésped titular" icon={<UserRound className="h-4 w-4" />}>
            <div className="mb-3"><BuscadorCliente clientes={clientes} seleccionado={clienteSel} onSelect={elegirCliente} />
              {clienteSel && fichaSel && <AvisosPerfil perfil={{ ...fichaSel.perfil, estadias: fichaSel.resumen?.estadias }} />}</div>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
              <Campo label="Nombre del huésped *" className="col-span-2 md:col-span-1"><input className={fieldCls} value={huesped.nombre} disabled={!!clienteSel} onChange={(e) => setHuesped({ ...huesped, nombre: e.target.value })} /></Campo>
              <Campo label="Tipo de identificación">
                <select className={fieldCls} value={huesped.tipo_identificacion} disabled={!!clienteSel} onChange={(e) => setHuesped({ ...huesped, tipo_identificacion: e.target.value })}>
                  {TIPOS_IDENT.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </Campo>
              <Campo label="Identificación"><input className={fieldCls} value={huesped.identificacion} disabled={!!clienteSel} onChange={(e) => setHuesped({ ...huesped, identificacion: e.target.value })} /></Campo>
              <Campo label="Email"><input type="email" className={fieldCls} value={huesped.email} onChange={(e) => setHuesped({ ...huesped, email: e.target.value })} /></Campo>
              <Campo label="Teléfono"><input className={fieldCls} value={huesped.telefono} onChange={(e) => setHuesped({ ...huesped, telefono: e.target.value })} /></Campo>
              <Campo label="Nacionalidad"><input className={fieldCls} value={huesped.nacionalidad} onChange={(e) => setHuesped({ ...huesped, nacionalidad: e.target.value })} /></Campo>
            </div>
            {coincide && (
              <div className="mt-2 flex flex-wrap items-center gap-2 rounded-md border border-sky-300 bg-sky-50 px-3 py-2 text-xs text-sky-900 dark:border-sky-800 dark:bg-sky-900/20 dark:text-sky-200">
                Esa identificación ya es del cliente <b>{coincide.razon_social}</b>.
                <button type="button" className="underline" onClick={() => elegirCliente(coincide)}>Usar ese cliente</button>
              </div>
            )}
            {!clienteSel && !huesped.identificacion.trim() && (
              <p className="mt-2 text-xs text-muted-foreground">
                Sin identificación la reserva queda solo a nombre del contacto (no se crea cliente). La cédula, RUC o pasaporte se
                pedirá al registrar el depósito o en el check-in, antes de facturar.
              </p>
            )}
          </Seccion>

          <Seccion n={3} titulo="Habitación y facturación" icon={<Building2 className="h-4 w-4" />}>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
              <Campo label="Canal">
                <select className={fieldCls} value={canal} onChange={(e) => setCanal(e.target.value)}>
                  {CANALES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </Campo>
              <Campo label="Empresa a facturar (opcional)" className="md:col-span-2">
                <select className={fieldCls} value={empresaId} onChange={(e) => setEmpresaId(e.target.value)}>
                  <option value="">— Factura al huésped —</option>
                  {clientes.map((c) => <option key={c.id} value={c.id}>{c.razon_social} ({c.identificacion ?? 's/i'})</option>)}
                </select>
              </Campo>
            </div>
            <div className="mt-4 space-y-1">
              <span className="text-xs font-medium text-muted-foreground">
                Habitación (opcional){tipoSel ? ` · ${habitacionesLibres.length} libre(s) de ${tipoSel.nombre}` : ''}
              </span>
              {!tipoId ? <p className="text-xs text-muted-foreground">Elige primero el tipo de habitación.</p> : (
                <div className="flex flex-wrap gap-1.5">
                  <button type="button" onClick={() => setHabitacionId('')}
                    className={`rounded border px-2.5 py-1 text-xs ${habitacionId === '' ? 'border-primary bg-primary text-primary-foreground' : 'hover:bg-muted'}`}>Asignar después</button>
                  {habitacionesLibres.map((h) => (
                    <button key={h.id} type="button" onClick={() => setHabitacionId(h.id)} title={h.piso ?? undefined}
                      className={`rounded border px-2.5 py-1 font-mono text-xs ${habitacionId === h.id ? 'border-primary bg-primary text-primary-foreground' : 'hover:bg-muted'}`}>
                      <BedDouble className="mr-1 inline h-3 w-3" />{h.numero}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <Campo label="Notas" className="mt-4"><textarea className={`${fieldCls} h-auto`} rows={2} value={notas} onChange={(e) => setNotas(e.target.value)} /></Campo>
          </Seccion>
        </div>

        {/* Resumen / cotización */}
        <div className="lg:sticky lg:top-4 lg:self-start">
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Resumen</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {!tipoId || !fechasValidas ? (
                <p className="text-muted-foreground">Elige fechas y tipo de habitación para ver la tarifa.</p>
              ) : errCot ? (
                <p className="text-red-600">{getErrorMessage(errCot)}</p>
              ) : !cot ? (
                <p className="text-muted-foreground">Cotizando…</p>
              ) : (
                <>
                  <div className={`text-xs text-muted-foreground ${cotizando ? 'opacity-50' : ''}`}>
                    {tipoSel?.nombre} · {fmtFecha(llegada)} → {fmtFecha(salida)} · {adultos} ad.{ninos ? ` + ${ninos} niñ.` : ''}
                  </div>
                  <div className="max-h-56 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead><tr className="text-left text-muted-foreground"><th className="py-1 pr-2">Noche</th><th className="py-1 pr-2 text-right">Precio</th><th className="py-1 text-right">IVA</th></tr></thead>
                      <tbody>
                        {cot.noches.map((n) => (
                          <Fragment key={n.fecha}>
                          <tr className={n.iva_pct === 8 ? 'bg-amber-50 dark:bg-amber-900/10' : ''}>
                            <td className="py-1 pr-2">{fmtFecha(n.fecha)}</td>
                            <td className="py-1 pr-2 text-right tabular-nums">{money(n.precio)}</td>
                            <td className="py-1 text-right tabular-nums">{n.iva_pct}%{n.iva_pct === 8 && <span className="ml-1 text-amber-600">feriado</span>}</td>
                          </tr>
                          {(n.componentes ?? []).length > 0 && (
                            <tr><td colSpan={3} className="pb-1 pl-3 text-[11px] text-muted-foreground">
                              + {(n.componentes ?? []).map((c) => `${c.nombre}${c.cantidad > 1 ? ` ×${c.cantidad}` : ''} ${money(c.monto)} (IVA ${c.iva_pct}%)`).join(' · ')}
                            </td></tr>
                          )}
                          </Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="space-y-1 border-t pt-2 text-xs">
                    {Number(cot.incluido_plan ?? 0) > 0 && <>
                      <div className="flex justify-between"><span>Alojamiento</span><span className="font-mono">{money(Number(cot.alojamiento))}</span></div>
                      <div className="flex justify-between"><span>Incluido en el plan</span><span className="font-mono">{money(Number(cot.incluido_plan))}</span></div>
                    </>}
                    <div className="flex justify-between"><span>Subtotal</span><span className="font-mono">{money(cot.subtotal)}</span></div>
                    <div className="flex justify-between"><span>IVA</span><span className="font-mono">{money(cot.iva)}</span></div>
                    {Number(cot.servicio) > 0 && <div className="flex justify-between"><span>Servicio</span><span className="font-mono">{money(cot.servicio)}</span></div>}
                    {Number(cot.tasa) > 0 && <div className="flex justify-between"><span>Tasa turística</span><span className="font-mono">{money(cot.tasa)}</span></div>}
                    <div className="flex justify-between border-t pt-1 text-base font-semibold"><span>Total</span><span className="font-mono">{money(cot.total)}</span></div>
                    <div className="flex justify-between text-amber-700 dark:text-amber-400"><span>Depósito requerido</span><span className="font-mono">{money(cot.deposito)}</span></div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {cot.politica.reembolsable ? 'Reembolsable' : 'No reembolsable'} · cancela con {cot.politica.cancel_dias} día(s)
                    (penalidad {cot.politica.cancel_penalidad_noches} noche(s)) · no-show {cot.politica.noshow_noches} noche(s)
                    {cot.politica.incluye_desayuno && ' · incluye desayuno'} · disponibles: {cot.disponibles_min}
                  </p>
                  {cot.errores.length > 0 && (
                    <div className="space-y-1 rounded-md border border-red-400 bg-red-50 px-3 py-2 text-xs text-red-800 dark:border-red-700 dark:bg-red-900/20 dark:text-red-300">
                      <div className="flex items-center gap-1 font-medium"><AlertTriangle className="h-3.5 w-3.5" /> No se puede reservar tal cual:</div>
                      <ul className="list-disc pl-5">{cot.errores.map((e, i) => <li key={i}>{e}</li>)}</ul>
                      {isAdmin() && (
                        <label className="flex items-center gap-2 pt-1">
                          <input type="checkbox" checked={forzar} onChange={(e) => setForzar(e.target.checked)} />
                          Forzar (administrador) — reservar de todas formas
                        </label>
                      )}
                    </div>
                  )}
                </>
              )}
              {faltan.length > 0 && <p className="text-xs text-muted-foreground">Falta: {faltan.join(', ')}.</p>}
              <div className="flex gap-2 border-t pt-3">
                <Button variant="outline" className="flex-1" onClick={onClose}>Cancelar</Button>
                <Button className="flex-1" isLoading={crear.isPending} disabled={!puedeReservar} onClick={() => crear.mutate()}>Reservar</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
